import {
  Stack,
  Button,
  Checkbox,
  TextField,
  FormControl,
  FormHelperText,
  FormControlLabel,
  CircularProgress
} from "@material-ui/core";
import { BulkMessageDialogTrialBanner } from "./BulkMessageDialogTrialBanner";
import { BulkMessageDialogFileUpload } from "./BulkMessageDialogFileUpload";
import { useTranslation } from "react-i18next";
import { FileOrFilePreview, FilePreview } from "../../../../../@types/listings/fileData";
import ListingsMatchRow from "components/_dashboard/listingsMatch/ListingsMatchRow";
import { BulkMessageDialogImageListingUpload } from "./BulkMessageDialogImageListingUpload";
import { UploadedFile } from "./NewBulkMessageDialog";
import { BulkMessageDialogTemplateManager } from "./BulkMessageDialogTemplateManager";
import { BulkMessageDialogTemplatePlaceholder } from "./BulkMessageDialogTemplatePlaceholder";
import type { BulkMessageRecipient } from "utils/contacts/bulkMessage/new";
import { type Dispatch, type RefObject, type ChangeEvent, type SetStateAction } from "react";
import {
  setInitialSelectedMessageStatus,
  type ActiveFieldTemplate,
  type BulkMessageDelaySettings,
  type BulkMessageTemplateType,
  type PlaceholderTemplateForm,
  type CustomTemplateField
} from "utils/contacts/bulkMessage/new";
import { BulkMessageDialogDelaySettings } from "./BulkMessageDialogDelaySettings";
import { BulkMessageDialogMessageTips } from "./BulkMessageDialogMessageTips";
import type { AlgoliaHit } from "instantsearch.js";
import type { ProcessedListingData } from "../../../../../@types/sharedSchema";

type BulkMessageDialogWriteMessageStepProps = {
  listing: AlgoliaHit<ProcessedListingData> | null;
  selected: string[];
  /** The recipients, as the dialog resolved them — see `NewBulkMessageDialog`. */
  audience: BulkMessageRecipient[];
  messageInput: string;
  snackbarOpen: boolean;
  filesPreview: FilePreview[];
  selectedFiles: FileOrFilePreview[];
  bulkMessageType: BulkMessageTemplateType;
  messageInputRef: RefObject<HTMLTextAreaElement>;
  showTrialBanner: boolean;
  activeTemplates: ActiveFieldTemplate;
  delaySettings: BulkMessageDelaySettings;
  placeholderTemplatesForm: PlaceholderTemplateForm;
  setMessageInput: Dispatch<SetStateAction<string>>;
  setFilesPreview: Dispatch<SetStateAction<FilePreview[]>>;
  setUploadedFile: Dispatch<SetStateAction<UploadedFile>>;
  setSelectedFiles: Dispatch<SetStateAction<FileOrFilePreview[]>>;
  setActiveTemplates: Dispatch<SetStateAction<ActiveFieldTemplate>>;
  setDelaySettings: Dispatch<SetStateAction<BulkMessageDelaySettings>>;
  setPlaceholderTemplatesForm: Dispatch<SetStateAction<PlaceholderTemplateForm>>;
  handleRemoveFile: (targetId: string) => () => void;
  handleFileUpload: ({ target: { files } }: ChangeEvent<HTMLInputElement>) => void;
  handleSnackbarClose: () => void;
  handleInsertTemplate: (template: string) => () => void;
  handleGetRandomMessage: () => void;
  customTemplateFields?: CustomTemplateField[];
  hideDefaultTemplates?: boolean;
};

export function BulkMessageDialogWriteMessageStep({
  listing,
  selected,
  audience,
  messageInput,
  filesPreview,
  snackbarOpen,
  selectedFiles,
  activeTemplates,
  bulkMessageType,
  showTrialBanner,
  messageInputRef,
  delaySettings,
  placeholderTemplatesForm,
  setMessageInput,
  setFilesPreview,
  setUploadedFile,
  setSelectedFiles,
  setActiveTemplates,
  setDelaySettings,
  setPlaceholderTemplatesForm,
  handleRemoveFile,
  handleFileUpload,
  handleSnackbarClose,
  handleInsertTemplate,
  handleGetRandomMessage,
  customTemplateFields,
  hideDefaultTemplates
}: BulkMessageDialogWriteMessageStepProps): JSX.Element {
  const { t: translate } = useTranslation();

  const handleChangeMessageInput = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    setMessageInput(value);
  };

  return (
    <Stack sx={{ height: "100%" }}>
      {showTrialBanner && <BulkMessageDialogTrialBanner />}
      {listing && <ListingsMatchRow listing={listing} inListingMatchToContacts />}
      <Stack sx={{ mt: 2 }} gap={2}>
        <BulkMessageDialogTemplateManager
          listing={listing}
          selected={selected}
          audience={audience}
          activeTemplates={activeTemplates}
          bulkMessageType={bulkMessageType}
          setActiveTemplates={setActiveTemplates}
          handleInsertTemplate={handleInsertTemplate}
          handleGetRandomMessage={handleGetRandomMessage}
          customTemplateFields={customTemplateFields}
          hideDefaultTemplates={hideDefaultTemplates}
        />
        <TextField
          fullWidth
          multiline
          value={messageInput}
          minRows={6}
          maxRows={6}
          inputRef={messageInputRef}
          placeholder={translate("contactsPage.bulkMessage.inputPlaceholder")}
          onChange={handleChangeMessageInput}
        />
        <BulkMessageDialogMessageTips message={messageInput} templateSystem="new" />
        <BulkMessageDialogFileUpload
          snackbarOpen={snackbarOpen}
          filesPreview={filesPreview}
          handleRemoveFile={handleRemoveFile}
          handleFileUpload={handleFileUpload}
          handleSnackbarClose={handleSnackbarClose}
        />
        {!!listing?.imageUrls?.length && (
          <BulkMessageDialogImageListingUpload
            listing={listing}
            filesPreview={filesPreview}
            selectedFiles={selectedFiles}
            setUploadedFile={setUploadedFile}
            setFilesPreview={setFilesPreview}
            setSelectedFiles={setSelectedFiles}
          />
        )}
        <BulkMessageDialogTemplatePlaceholder
          selected={selected}
          audience={audience}
          messageInput={messageInput}
          bulkMessageType={bulkMessageType}
          placeholderTemplatesForm={placeholderTemplatesForm}
          setPlaceholderTemplatesForm={setPlaceholderTemplatesForm}
        />
        <BulkMessageDialogDelaySettings
          delaySettings={delaySettings}
          setDelaySettings={setDelaySettings}
        />
      </Stack>
    </Stack>
  );
}

type BulkMessageDialogWriteMessageStepActionsProps = {
  selected: string[];
  /** The recipients, as the dialog resolved them — see `NewBulkMessageDialog`. */
  audience: BulkMessageRecipient[];
  /**
   * The recipients are still being looked up. "Next" waits: the skip rules (opted out,
   * messaged recently) are decided from them, and a send prepared a moment early would be
   * prepared against recipients nothing is known about yet.
   */
  loadingAudience: boolean;
  messageInput: string;
  filterRecentContactsBulkMessages: boolean;
  setFilterRecentContactsBulkMessages: Dispatch<SetStateAction<boolean>>;
  handleNextStep: () => void;
};

export function BulkMessageDialogWriteMessageStepActions({
  selected,
  audience,
  loadingAudience,
  messageInput,
  filterRecentContactsBulkMessages,
  setFilterRecentContactsBulkMessages,
  handleNextStep
}: BulkMessageDialogWriteMessageStepActionsProps): JSX.Element {
  const { t: translate } = useTranslation();

  const handleToggleFilterRecentContactsBulkMessages = (): void => {
    setFilterRecentContactsBulkMessages(!filterRecentContactsBulkMessages);
  };

  const filteredSelectedMessageStatus = setInitialSelectedMessageStatus(selected, audience, true);

  const isAllContactsSkipped = Object.values(filteredSelectedMessageStatus).every(
    (status) => status.status === "skipped"
  );

  let isNextDisabled = messageInput.length === 0 || loadingAudience;

  if (filterRecentContactsBulkMessages) isNextDisabled = isNextDisabled || isAllContactsSkipped;

  return (
    <Stack
      gap={2}
      sx={{ mt: "auto" }}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <FormControl component="fieldset" variant="standard">
        <FormControlLabel
          control={
            <Checkbox
              checked={filterRecentContactsBulkMessages}
              onChange={handleToggleFilterRecentContactsBulkMessages}
            />
          }
          label={translate(
            "contactsPage.bulkMessage.filterOutContactsThatHaveReceivedMessageInLast30Days"
          )}
        />
        {filterRecentContactsBulkMessages && isAllContactsSkipped && (
          <FormHelperText sx={{ mt: 0 }}>
            {translate(
              "contactsPage.bulkMessage.filterOutContactsThatHaveReceivedMessageInLast30DaysDisabled"
            )}
          </FormHelperText>
        )}
      </FormControl>
      <Button
        variant="contained"
        disabled={isNextDisabled}
        // Says WHY it is disabled for the moment the recipients are being looked up.
        startIcon={loadingAudience ? <CircularProgress size={16} color="inherit" /> : undefined}
        onClick={handleNextStep}
      >
        {translate("contactsPage.control.next")}
      </Button>
    </Stack>
  );
}
